/**
 * MigrationChecklist — the QuickBooks → CountBucks Migration Hub.
 *
 * Renders the 9-report checklist with:
 *   • Top progress strip (X of Y required reports done)
 *   • One <ReportUploadCard> per report (CSV / Excel / PDF / image)
 *   • Tie-out variance report at the bottom (locks when at least
 *     Trial Balance is uploaded)
 *
 * AI usage is metered against the workspace credit pool — PDF / image
 * uploads pre-debit before OCR runs (refund on failure). CSV + Excel
 * are free.
 *
 * Lives inside /switch-from-quickbooks (the existing MigrationHub
 * page) and replaces the prior wizard-style flow for users who'd
 * rather upload reports as-they-have-them rather than follow a
 * linear wizard.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { useWorkspace } from '../../context/WorkspaceContext';
import { apiFetch } from '../../fetchInterceptor';
import { toast } from 'sonner';
import {
  ListChecks, FileSpreadsheet, Users, Landmark, Receipt, FileBarChart,
  TrendingUp, CheckCircle2, ChevronDown, ChevronUp, Upload, Loader2,
  AlertCircle, Sparkles, FileText,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Report metadata: order, icon, description, QB export instructions ──
const REPORTS = [
  {
    id: 'chart_of_accounts',
    label: 'Chart of Accounts',
    icon: ListChecks,
    required: true,
    description: 'Your full list of GL accounts — used to map every other report.',
    qbInstructions: 'In QuickBooks: Reports → Account List (or Chart of Accounts) → Customize columns to include Account, Type, Number → Export to CSV / Excel.',
  },
  {
    id: 'customer_list',
    label: 'Customer List',
    icon: Users,
    required: false,
    description: 'Brings over every customer with email, phone, and address.',
    qbInstructions: 'In QuickBooks: Sales → Customers → ⤓ Export to Excel (include all columns).',
  },
  {
    id: 'vendor_list',
    label: 'Vendor List',
    icon: Users,
    required: false,
    description: 'Brings over every vendor for AP + 1099 prep.',
    qbInstructions: 'In QuickBooks: Expenses → Vendors → ⤓ Export to Excel (include Tax ID column for 1099s).',
  },
  {
    id: 'trial_balance',
    label: 'Trial Balance (at cutover)',
    icon: Landmark,
    required: true,
    description: 'Opening balances. One opening journal entry is created from this.',
    qbInstructions: 'In QuickBooks: Reports → Trial Balance → set date = your cutover date (usually last day of last completed month) → Export.',
  },
  {
    id: 'transaction_detail',
    label: 'Transaction Detail by Account',
    icon: FileBarChart,
    required: false,
    description: 'Every post-cutover transaction so CountBucks can replay them.',
    qbInstructions: 'In QuickBooks: Reports → Transaction Detail by Account → date = cutover-date+1 → today → Export.',
  },
  {
    id: 'ar_aging',
    label: 'A/R Aging Summary',
    icon: Receipt,
    required: false,
    description: 'Brings open invoices forward so collections continue uninterrupted.',
    qbInstructions: 'In QuickBooks: Reports → A/R Aging Summary → date = cutover date → Export.',
  },
  {
    id: 'ap_aging',
    label: 'A/P Aging Summary',
    icon: Receipt,
    required: false,
    description: 'Brings open bills forward so payment scheduling continues.',
    qbInstructions: 'In QuickBooks: Reports → A/P Aging Summary → date = cutover date → Export.',
  },
  {
    id: 'prior_pnl',
    label: 'Prior-year Profit & Loss',
    icon: TrendingUp,
    required: false,
    description: 'Powers the "vs last year" comparison on the dashboard.',
    qbInstructions: 'In QuickBooks: Reports → Profit & Loss → date = Jan 1 to Dec 31 of last year → Export.',
  },
  {
    id: 'prior_balance_sheet',
    label: 'Prior-year Balance Sheet',
    icon: FileSpreadsheet,
    required: false,
    description: 'Powers the comparative Balance Sheet column.',
    qbInstructions: 'In QuickBooks: Reports → Balance Sheet → as of Dec 31 of last year → Export.',
  },
];

const STATUS_BADGE = {
  not_started: { label: 'Not started', tone: 'zinc' },
  uploaded:    { label: 'Preview ready', tone: 'amber' },
  committed:   { label: 'Imported ✓', tone: 'emerald' },
};


export default function MigrationChecklist({ workspaceId: workspaceIdProp } = {}) {
  // Prefer the explicit prop (passed by MigrationHub.jsx) so we render
  // immediately on mount; fall back to context for any direct embedders.
  const { workspace } = useWorkspace();
  const workspaceId = workspaceIdProp || workspace?.workspace_id;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [openId, setOpenId] = useState(null);  // which card is expanded

  const loadStatus = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const r = await apiFetch(`${API_URL}/api/migration/v3/status?workspace_id=${workspaceId}`);
      if (r.ok) {
        setStatus(await r.json());
        setLoadError(null);
      } else {
        const err = await r.json().catch(() => ({}));
        setLoadError(err.detail || `Could not load checklist (HTTP ${r.status})`);
      }
    } catch (e) {
      // Don't silently swallow — surface a friendly error so the user
      // knows the checklist isn't just empty by design.
      console.warn('migration checklist load failed', e);
      setLoadError('Network error loading checklist — please refresh.');
    } finally { setLoading(false); }
  }, [workspaceId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  if (!workspaceId) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center" data-testid="migration-checklist-no-workspace">
        Select a workspace to view your migration checklist.
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-500 py-8" data-testid="migration-checklist-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading migration status…
      </div>
    );
  }
  if (loadError) {
    return (
      <Card data-testid="migration-checklist-error">
        <CardContent className="py-6 text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {loadError}
        </CardContent>
      </Card>
    );
  }

  const percent = status?.percent || 0;
  const completedRequired = status?.completed_required || 0;
  const totalRequired = status?.total_required || 0;

  return (
    <div className="space-y-4" data-testid="migration-checklist">
      {/* ── Top progress strip ─────────────────────────────────────── */}
      <Card data-testid="migration-progress-strip">
        <CardContent className="pt-6 pb-5">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-medium">Migration progress</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {completedRequired} of {totalRequired} required reports imported · {status?.reports?.filter(r => r.status === 'committed').length || 0} of 9 total
              </div>
            </div>
            <div className="flex items-center gap-3">
              {status?.overall_confidence != null && (
                <div
                  className="text-right"
                  data-testid="migration-overall-confidence"
                  title="Average confidence across all imported reports. CSV/Excel = 100%, PDF/image = Gemini's OCR confidence."
                >
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Confidence
                  </div>
                  <div className={cn(
                    'text-lg font-semibold leading-none',
                    status.overall_confidence >= 95 ? 'text-emerald-600 dark:text-emerald-400'
                      : status.overall_confidence >= 80 ? 'text-amber-600 dark:text-amber-400'
                      : 'text-rose-600 dark:text-rose-400',
                  )}>
                    {status.overall_confidence}%
                  </div>
                </div>
              )}
              <div className="text-2xl font-semibold text-emerald-600 dark:text-emerald-400" data-testid="migration-percent">
                {percent}%
              </div>
            </div>
          </div>
          <Progress value={percent} className="h-2" />
          {status?.all_required_done && (
            <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
              <CheckCircle2 className="w-4 h-4" /> Required reports complete — review the tie-out report below to sign off.
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Per-report checklist ───────────────────────────────────── */}
      <div className="space-y-2">
        {REPORTS.map((meta) => {
          const rep = status?.reports?.find(r => r.id === meta.id);
          const isOpen = openId === meta.id;
          return (
            <ReportUploadCard
              key={meta.id}
              meta={meta}
              report={rep}
              workspaceId={workspaceId}
              isOpen={isOpen}
              onToggle={() => setOpenId(isOpen ? null : meta.id)}
              onChange={loadStatus}
            />
          );
        })}
      </div>

      {/* ── Tie-out variance report ────────────────────────────────── */}
      <TieOutCard workspaceId={workspaceId} statusRefresh={loadStatus} />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
//  ReportUploadCard — single row of the checklist
// ═══════════════════════════════════════════════════════════════════════
function ReportUploadCard({ meta, report, workspaceId, isOpen, onToggle, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [commitBusy, setCommitBusy] = useState(false);
  const [showInstr, setShowInstr] = useState(false);
  const Icon = meta.icon;
  const status = report?.status || 'not_started';
  const badge = STATUS_BADGE[status];

  // Load any previous upload so the user sees their preview after a refresh
  useEffect(() => {
    if (!isOpen || preview || status === 'not_started') return;
    let cancelled = false;
    (async () => {
      const r = await apiFetch(`${API_URL}/api/migration/v3/upload/${meta.id}?workspace_id=${workspaceId}`);
      if (r.ok && !cancelled) {
        const d = await r.json();
        if (d.rows?.length) setPreview(d);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, status, meta.id, workspaceId, preview]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('workspace_id', workspaceId);
      fd.append('report_type', meta.id);
      const r = await apiFetch(`${API_URL}/api/migration/v3/upload-and-extract`, {
        method: 'POST',
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        if (r.status === 402) {
          toast.error(err.detail || 'Out of AI credits — top up to use OCR on PDF / images.');
        } else {
          toast.error(err.detail || `Upload failed (HTTP ${r.status})`);
        }
        return;
      }
      const data = await r.json();
      setPreview(data);
      if (data.ai_cost_credits > 0) {
        toast.success(`Parsed ${data.row_count} rows · used ${data.ai_cost_display} for AI OCR`);
      } else {
        toast.success(`Parsed ${data.row_count} rows from your ${meta.label}`);
      }
      onChange?.();
    } catch (e) {
      toast.error('Upload failed — please try again.');
    } finally {
      setUploading(false);
    }
  };

  const commit = async () => {
    if (!preview?.rows?.length) return;
    setCommitBusy(true);
    try {
      // Each report type has its own commit path; aging endpoints live in v3,
      // everything else flows through the legacy commit endpoints which the
      // wizard already exercised so we don't break the existing import path.
      let endpoint;
      let payload;
      if (meta.id === 'ar_aging') {
        endpoint = `${API_URL}/api/migration/v3/import/ar-aging`;
        payload = { workspace_id: workspaceId, rows: preview.rows };
      } else if (meta.id === 'ap_aging') {
        endpoint = `${API_URL}/api/migration/v3/import/ap-aging`;
        payload = { workspace_id: workspaceId, rows: preview.rows };
      } else if (meta.id === 'prior_pnl' || meta.id === 'prior_balance_sheet') {
        endpoint = `${API_URL}/api/migration/import/comparative`;
        payload = {
          workspace_id: workspaceId,
          statement_type: meta.id === 'prior_pnl' ? 'pnl' : 'balance_sheet',
          period_year: new Date().getFullYear() - 1,
          rows: preview.rows.map(r => ({ label: r.label, amount: r.amount, category: r.category })),
        };
      } else if (meta.id === 'customer_list' || meta.id === 'vendor_list') {
        // Light-weight contact bulk-insert; pipes into the existing
        // /import/contacts endpoint via a CSV-like payload it understands.
        const contactType = meta.id === 'customer_list' ? 'customer' : 'vendor';
        const contacts = preview.rows.map(r => ({
          name: r.label, email: r.email, phone: r.phone,
          address: r.address, type: contactType,
          tax_id: r.tax_id || null,
        }));
        const r = await apiFetch(`${API_URL}/api/migration/v3/import/contacts-json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workspace_id: workspaceId, contacts, type: contactType }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          toast.error(err.detail || `Commit failed (HTTP ${r.status})`);
          return;
        }
        const d = await r.json();
        toast.success(`${d.imported || contacts.length} ${contactType}s imported`);
        onChange?.();
        return;
      } else if (meta.id === 'chart_of_accounts') {
        endpoint = `${API_URL}/api/migration/v3/import/chart-of-accounts-json`;
        payload = { workspace_id: workspaceId, rows: preview.rows };
      } else if (meta.id === 'trial_balance') {
        endpoint = `${API_URL}/api/migration/v3/import/trial-balance-json`;
        // Cutover date defaults to the report's as_of_date or today if missing
        const cutover = preview.as_of_date || new Date().toISOString().slice(0, 10);
        payload = { workspace_id: workspaceId, cutover_date: cutover, rows: preview.rows };
      } else if (meta.id === 'transaction_detail') {
        endpoint = `${API_URL}/api/migration/v3/import/transactions-json`;
        payload = { workspace_id: workspaceId, rows: preview.rows };
      } else {
        toast.info(`Commit for ${meta.label} runs through the wizard for now.`);
        return;
      }
      const r = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Commit failed (HTTP ${r.status})`);
        return;
      }
      const d = await r.json();
      // Flip the v3 status for legacy commit paths so the progress
      // bar updates without a page refresh.
      await apiFetch(`${API_URL}/api/migration/v3/mark-committed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, report_type: meta.id }),
      }).catch(() => {});
      toast.success(`✓ ${meta.label} imported (${d.created || d.saved_rows || preview.row_count} rows)`);
      onChange?.();
    } finally {
      setCommitBusy(false);
    }
  };

  return (
    <Card
      className={cn(
        'transition-colors',
        status === 'committed' && 'border-emerald-500/30 bg-emerald-500/[0.02]',
      )}
      data-testid={`migration-card-${meta.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors"
        data-testid={`migration-card-${meta.id}-toggle`}
      >
        <div className={cn(
          'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
          status === 'committed' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
            : status === 'uploaded' ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
            : 'bg-zinc-500/10 text-zinc-500',
        )}>
          {status === 'committed' ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{meta.label}</span>
            {meta.required && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">Required</Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {report?.row_count != null
              ? `${report.row_count} rows · ${report.filename}`
              : meta.description}
          </div>
        </div>
        <Badge
          variant={badge.tone === 'emerald' ? 'default' : 'outline'}
          className={cn(
            'text-[10px]',
            badge.tone === 'emerald' && 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
            badge.tone === 'amber' && 'border-amber-500/30 text-amber-600 dark:text-amber-400',
          )}
          data-testid={`migration-card-${meta.id}-status`}
        >
          {badge.label}
        </Badge>
        {report?.confidence != null && status !== 'not_started' && (
          <span
            className={cn(
              'text-[10px] px-1.5 py-0.5 rounded font-medium',
              report.confidence >= 95
                ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                : report.confidence >= 80
                ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                : 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
            )}
            title={`Confidence score: ${report.confidence}%. ${
              report.format === 'csv' || report.format === 'excel'
                ? 'Structured upload — no AI guessing.'
                : 'Gemini vision OCR confidence — manually review if < 95%.'
            }`}
            data-testid={`migration-card-${meta.id}-confidence`}
          >
            {report.confidence}%
          </span>
        )}
        {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>

      {isOpen && (
        <CardContent className="border-t pt-4 space-y-3" data-testid={`migration-card-${meta.id}-body`}>
          {/* QB export instructions */}
          <button
            type="button"
            onClick={() => setShowInstr(s => !s)}
            className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1"
          >
            <FileText className="w-3 h-3" />
            How to export from QuickBooks
            {showInstr ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showInstr && (
            <div className="text-xs text-muted-foreground bg-zinc-50 dark:bg-white/[0.02] rounded p-3 border border-zinc-200 dark:border-white/5">
              {meta.qbInstructions}
            </div>
          )}

          {/* Drop zone */}
          <label
            className={cn(
              'block border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
              uploading
                ? 'border-emerald-500/40 bg-emerald-500/5'
                : 'border-zinc-300 dark:border-white/10 hover:border-emerald-500/40 hover:bg-emerald-500/5',
            )}
            data-testid={`migration-card-${meta.id}-dropzone`}
          >
            <input
              type="file"
              className="hidden"
              accept=".csv,.xlsx,.xls,.pdf,.png,.jpg,.jpeg,.webp"
              disabled={uploading}
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid={`migration-card-${meta.id}-input`}
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300">
                <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
                Parsing…
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1 text-sm text-zinc-600 dark:text-zinc-400">
                <Upload className="w-5 h-5 text-emerald-500" />
                <span>Drop CSV · Excel · PDF · or image — or click to browse</span>
                <span className="text-[11px] text-muted-foreground mt-1">
                  PDF / image runs through AI OCR (charges credits)
                </span>
              </div>
            )}
          </label>

          {/* Preview + Commit */}
          {preview?.rows?.length > 0 && (
            <PreviewTable rows={preview.rows} reportType={meta.id} />
          )}
          {preview?.rows?.length > 0 && status !== 'committed' && (
            <div className="flex justify-end">
              <Button
                onClick={commit}
                disabled={commitBusy}
                size="sm"
                className="gap-1.5"
                data-testid={`migration-card-${meta.id}-commit`}
              >
                {commitBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Import {preview.rows.length} rows into CountBucks
              </Button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}


// ─── Preview table (first 10 rows) ──────────────────────────────────────
function PreviewTable({ rows, reportType }) {
  if (!rows?.length) return null;
  const sample = rows.slice(0, 10);
  // Choose columns based on report shape
  const cols = Object.keys(sample[0] || {}).filter(k => k !== 'amount' || sample.some(r => r.amount != null)).slice(0, 5);
  return (
    <div className="overflow-x-auto rounded border border-zinc-200 dark:border-white/10" data-testid={`migration-preview-${reportType}`}>
      <table className="w-full text-xs">
        <thead className="bg-zinc-50 dark:bg-white/[0.03]">
          <tr>
            {cols.map(c => (
              <th key={c} className="px-3 py-2 text-left font-medium text-zinc-600 dark:text-zinc-400 capitalize">
                {c.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sample.map((r, i) => (
            <tr key={i} className="border-t border-zinc-100 dark:border-white/5">
              {cols.map(c => (
                <td key={c} className="px-3 py-1.5 text-zinc-700 dark:text-zinc-300">
                  {typeof r[c] === 'number' ? r[c].toLocaleString(undefined, { maximumFractionDigits: 2 }) : (r[c] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 10 && (
        <div className="px-3 py-1.5 text-[11px] text-muted-foreground border-t border-zinc-100 dark:border-white/5">
          + {rows.length - 10} more rows
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
//  TieOutCard — variance check QB totals vs CountBucks
// ═══════════════════════════════════════════════════════════════════════
function TieOutCard({ workspaceId, statusRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/migration/v3/tie-out-report?workspace_id=${workspaceId}`);
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  };

  return (
    <Card className="mt-6" data-testid="migration-tieout-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-500" /> Tie-out report
          </CardTitle>
          <Button onClick={run} disabled={loading} size="sm" variant="outline" data-testid="migration-tieout-run">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Run check'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Compares what QuickBooks said vs what CountBucks now has. Each line should match to the penny.
        </p>
      </CardHeader>
      <CardContent>
        {!data && (
          <div className="text-sm text-muted-foreground py-3">
            Click <strong>Run check</strong> after importing your Trial Balance + A/R + A/P to verify everything ties.
          </div>
        )}
        {data && (
          <div className="space-y-2" data-testid="migration-tieout-results">
            {data.checks.map((c, i) => (
              <div
                key={i}
                className={cn(
                  'flex items-center justify-between text-sm py-2 px-3 rounded',
                  c.ok
                    ? 'bg-emerald-500/5 text-emerald-700 dark:text-emerald-400'
                    : 'bg-amber-500/5 text-amber-700 dark:text-amber-400',
                )}
                data-testid={`migration-tieout-check-${i}`}
              >
                <div className="flex items-center gap-2">
                  {c.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span>{c.label}</span>
                </div>
                <div className="font-mono text-xs">
                  QB ${c.qb_total.toLocaleString()} · CB ${c.cb_total.toLocaleString()}
                  {!c.ok && <span className="ml-2 font-semibold">Δ ${c.diff.toLocaleString()}</span>}
                </div>
              </div>
            ))}
            <div className={cn(
              'text-sm font-medium pt-2 border-t mt-2',
              data.overall_ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400',
            )}>
              {data.overall_ok ? '✓ All checks pass — migration ties out cleanly.' : 'Some variance detected. Review the lines above before signing off.'}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
